/**
 * @feature quote-requests
 * @layer presentation/components/utils
 *
 * Formatter Utilities
 * Formatting functions for displaying quote data
 */

import type { ItemData, CategoryType, LabelValuePair } from "../types";
import { BUDGET_TYPE_LABELS } from "../types";
import { formatDateKoreanLong } from "@semo/shared-utils";
import { getCategoryStyles } from "../../../utils";

// ============================================================================
// Date Formatting
// ============================================================================

/**
 * Format date for display
 * @param dateString - ISO date string
 * @returns Formatted date (e.g., "2024년 12월 24일") or '미정'
 */
export function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return "미정";
  const result = formatDateKoreanLong(dateString);
  return result || "미정";
}

/**
 * Format due date with flexibility (date, relative, or fixed days)
 * Handles different due date types: fixed date, relative (after days), or flexible
 *
 * @param dueDate - Fixed due date ISO string
 * @param dueDateType - Type: 'fixed', 'flexible', 'days'
 * @param dueDateValue - Value indicating type: '구체적' (specific date), '협의가능' (negotiable), or relative (e.g., "2주")
 * @param dueDateDays - Number of days if type is 'days'
 * @returns Formatted due date string
 */
export function formatDueDate(
  dueDate: string | null | undefined,
  dueDateType: string | null | undefined,
  dueDateValue: string | null | undefined,
  dueDateDays: number | null | undefined,
): string {
  // Handle '협의가능' (negotiable) - from Step3 form
  if (dueDateValue === "협의가능") {
    return "계약 후 협의 가능";
  }

  // Handle '구체적' (specific date) - should show actual date
  if (dueDateValue === "구체적") {
    if (dueDate) {
      return formatDate(dueDate);
    }
    return "날짜 미선택";
  }

  // Priority: fixed date > relative value > days > undefined
  if (dueDate) {
    return formatDate(dueDate);
  }

  // Relative value (e.g., "2주", "1개월")
  if (dueDateValue) {
    return `${dueDateValue} 이내`;
  }

  if (dueDateDays !== null && dueDateDays !== undefined && dueDateDays > 0) {
    return `${dueDateDays}일 이내`;
  }

  if (dueDateType === "flexible") {
    return "조율 가능";
  }

  return "미정";
}

// ============================================================================
// Currency & Amount Formatting
// ============================================================================

/**
 * Format amount as currency
 * @param amount - Amount in KRW
 * @returns Formatted currency (e.g., "1,000,000원") or '미정'
 */
export function formatAmount(amount: number | null | undefined): string {
  if (!amount) return "미정";

  return new Intl.NumberFormat("ko-KR", {
    style: "currency",
    currency: "KRW",
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Format budget display with type
 * @param budgetAmount - Amount in KRW
 * @param budgetType - Type (e.g., 'exact', 'range', 'no_budget')
 * @returns Formatted budget string
 */
export function formatBudget(
  budgetAmount: number | null | undefined,
  budgetType: string | null | undefined,
): string {
  if (budgetType === "no_budget" || !budgetType) {
    return "예산 미정";
  }

  const label = BUDGET_TYPE_LABELS[budgetType] ?? budgetType;

  if (!budgetAmount) {
    return label;
  }

  return `${formatAmount(budgetAmount)} (${label})`;
}

/**
 * Format budget visibility status
 * @param budgetOption - 'public', 'PUBLIC', or any other value
 * @returns Human-readable status
 */
export function formatBudgetVisibility(
  budgetOption: string | null | undefined,
): string {
  if (budgetOption === "public" || budgetOption === "PUBLIC") {
    return "업체에 공개됨";
  }

  return "업체에 비공개";
}

// ============================================================================
// Category & Type Formatting
// ============================================================================

/**
 * Format category type for display
 * @param categoryType - Category type
 * @returns Formatted label (e.g., "물품 구매")
 */
export function formatCategory(
  categoryType: CategoryType | string | null | undefined,
): string {
  if (!categoryType) return "카테고리 미선택";

  return getCategoryStyles(categoryType).label;
}

/**
 * Format option value for display
 * @param option - Option object (LabelValuePair from Swagger) or string
 * @returns Label string
 */
export function formatOption(
  option: LabelValuePair | string | null | undefined,
): string {
  if (!option) return "-";

  if (typeof option === "string") return option;

  return option.label || option.value || "-";
}

/**
 * Format array of options for display
 * @param options - Array of option values (LabelValuePair[] from Swagger)
 * @param separator - Separator between items (default: ', ')
 * @returns Formatted string (e.g., "옵션1, 옵션2")
 */
export function formatOptions(
  options: LabelValuePair[] | string[] | null | undefined,
  separator = ", ",
): string {
  if (!Array.isArray(options) || options.length === 0) {
    return "-";
  }

  return options.map((opt) => formatOption(opt)).join(separator);
}

// ============================================================================
// Item Formatting
// ============================================================================

/**
 * Format item name for display
 * @param item - Item object
 * @returns Item name or '-'
 */
export function formatItemName(item: ItemData | null | undefined): string {
  if (!item) return "-";

  return item.name ?? item.productName ?? "-";
}

/**
 * Format item specification for display
 * @param item - Item object
 * @returns Item specification or '-'
 */
export function formatItemSpec(item: ItemData | null | undefined): string {
  if (!item) return "-";

  return item.specification ?? item.spec ?? "-";
}

/**
 * Format quantity with unit
 * @param quantity - Quantity number
 * @param unit - Unit string
 * @returns Formatted quantity (e.g., "10개") or '-'
 */
export function formatQuantity(
  quantity: number | null | undefined,
  unit: string | null | undefined,
): string {
  if (quantity === null || quantity === undefined) return "-";

  const unitStr = unit ?? "개";
  return `${quantity.toLocaleString()}${unitStr}`;
}

/**
 * Format item for table display
 * @param item - Item object
 * @param index - Item index (for row number)
 * @returns Object with formatted fields
 */
export function formatItemRow(item: ItemData, index: number) {
  return {
    no: index + 1,
    name: formatItemName(item),
    spec: formatItemSpec(item),
    quantity: formatQuantity(item.quantity, item.unit),
  };
}

// ============================================================================
// Address Formatting
// ============================================================================

/**
 * Format address for display
 * @param addressAlias - Address alias/label
 * @param roadAddress - Road address
 * @param address - Alternative address format
 * @returns Formatted address string
 */
export function formatAddress(
  addressAlias: string | null | undefined,
  roadAddress: string | null | undefined,
  address: string | null | undefined,
): string {
  const label = addressAlias ?? "등록된 배송지";
  const fullAddress = roadAddress ?? address ?? "주소 정보 없음";

  return `${label}\n${fullAddress}`;
}

/**
 * Format complete address details
 *
 * 도로명 주소가 있으면 «도로명 + 상세 주소» 를, 없으면(공급사에게 가려진 응답 등)
 * 시·도/시·군·구/동만 보여 준다. 예전엔 늘 시·구·동만 보여 고른 배송지를 확인할 수
 * 없었다(GS A2-24).
 *
 * @param data - Address data object
 * @returns Formatted address string
 */
export function formatAddressDetails(
  data: Record<string, unknown> | null | undefined,
): string {
  if (!data) return "배송지 미등록";

  const text = (value: unknown): string =>
    typeof value === "string" ? value.trim() : "";

  const road = text(data.roadAddress);
  if (road) {
    return [road, text(data.addressDetail)].filter(Boolean).join(" ");
  }

  const parts = [data.province, data.city, data.district];

  const address = parts.filter((p) => p && typeof p === "string").join(" ");

  if (!address) return "주소 정보 없음";

  return address;
}

/**
 * Format recipient info
 * @param name - Recipient name
 * @param phone - Recipient phone
 * @returns Formatted string (e.g., "홍길동 / 010-1234-5678")
 */
export function formatRecipient(
  name: string | null | undefined,
  phone: string | null | undefined,
): string {
  const parts = [name, phone].filter((p) => p && typeof p === "string");

  return parts.length > 0 ? parts.join(" / ") : "-";
}

// ============================================================================
// Contact Formatting
// ============================================================================

/**
 * Format manager/contact info
 * @param name - Name
 * @param phone - Phone number
 * @returns Formatted contact string or '-'
 */
export function formatContact(
  name: string | null | undefined,
  phone: string | null | undefined,
): string {
  if (name && phone) {
    return `${name} (${phone})`;
  }

  return name ?? phone ?? "-";
}

// ============================================================================
// File Formatting
// ============================================================================

/**
 * Format file size for display
 * @param bytes - File size in bytes
 * @returns Formatted size (e.g., "1.5 MB")
 */
export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes || bytes === 0) return "0 B";

  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

/**
 * Format file name for display (truncate if too long)
 * @param fileName - File name
 * @param maxLength - Maximum length (default: 40)
 * @returns Truncated file name with ellipsis if needed
 */
export function formatFileName(
  fileName: string | null | undefined,
  maxLength = 40,
): string {
  if (!fileName) return "-";

  if (fileName.length <= maxLength) return fileName;

  const truncated = fileName.substring(0, maxLength - 3);
  return truncated + "...";
}

// ============================================================================
// Boolean Status Formatting
// ============================================================================

/**
 * Format boolean as Korean status
 * @param value - Boolean value
 * @param trueLabel - Label when true (default: '예')
 * @param falseLabel - Label when false (default: '아니오')
 * @returns Formatted label
 */
export function formatBoolean(
  value: boolean | null | undefined,
  trueLabel = "예",
  falseLabel = "아니오",
): string {
  if (value === null || value === undefined) return "-";

  return value ? trueLabel : falseLabel;
}

/**
 * Format same region only flag
 * @param value - Boolean value
 * @returns Formatted status
 */
export function formatSameRegionOnly(
  value: boolean | null | undefined,
): string {
  return formatBoolean(value, "같은 지역만", "지역 제한 없음");
}

// ============================================================================
// Null/Empty Handling
// ============================================================================

/**
 * Format value with fallback
 * @param value - Value to format
 * @param fallback - Fallback value (default: '-')
 * @returns Formatted value or fallback
 */
export function formatWithFallback(value: unknown, fallback = "-"): string {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  if (typeof value === "boolean") return formatBoolean(value);

  return fallback;
}
